import { isNil, throttle } from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";

import sliderTransitions from "../../constants/sliderTransitions";
import timeoutRates from "../../constants/timeoutRates";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import dateByAddingSpan from "../../modules/definition/calendar/dateFunctions/dateByAddingSpan";
import dateWithComponents from "../../modules/definition/calendar/dateFunctions/dateWithComponents";
import dateWithTimestamp from "../../modules/definition/calendar/dateFunctions/dateWithTimestamp";
import { getEraName } from "../../modules/definition/calendar/dateFunctions/stringFromDate";
import { getDaysInMonth } from "../../modules/definition/calendar/selectors";
import { Precision, PropTypeShapes } from "../../types";
import { TimespanRecord } from "../../types/immutableRecords/model/data/TimespanRecord";
import PageSlider from "../pageSlider";
import { NUM_OF_ERA_IN_ROW, NUM_OF_MONTH_IN_ROW, NUM_OF_YEAR_IN_GRID, NUM_OF_YEAR_IN_ROW } from "./constants";

const getEra = (eraIndex, calendar) => calendar.eras.get(eraIndex);

const getGridItemsStyle = numberInRow => ({ gridTemplateColumns: `repeat(${numberInRow}, 1fr)` });

const getGridIndex = (date, precision, calendar) => {
  const isEraBackward = getEra(date.era, calendar).isBackwards;
  let index = 0;
  if (precision === Precision.Year) {
    index = isEraBackward
      ? -Math.ceil(date.year / NUM_OF_YEAR_IN_GRID)
      : Math.floor((date.year - 1) / NUM_OF_YEAR_IN_GRID);
  } else if (precision === Precision.Month) {
    index = isEraBackward ? -date.year : date.year;
  } else if (precision >= Precision.Day) {
    const monthCount = calendar.months.size;
    index = isEraBackward ? -(date.year * monthCount - date.month + 1) : (date.year - 1) * monthCount + date.month;
  }
  return index;
};

const getEraItems = (showDate, selectedDate, onChange, calendar) => {
  const items = calendar.eras.map((era, eraIndex) => {
    const isSelected = isDateValid(selectedDate) && selectedDate.era === eraIndex;
    return (
      <div
        key={eraIndex}
        className={`gridItem${isSelected ? " selected" : ""}`}
        onClick={() => onChange({ era: eraIndex })}
      >
        {getEraName(calendar, eraIndex)}
      </div>
    );
  });
  return (
    <div style={getGridItemsStyle(NUM_OF_ERA_IN_ROW)} className={`gridItems`}>
      {items}
    </div>
  );
};

const getYearItems = (showDate, selectedDate, onChange, calendar) => {
  // This may not be right when eras setup is various
  const { isBackwards: isEraBackward, duration: eraDuration } = getEra(showDate.era, calendar);

  const firstYearOfGrid = isEraBackward
    ? Math.abs(-Math.ceil(showDate.year / NUM_OF_YEAR_IN_GRID) + 1) * NUM_OF_YEAR_IN_GRID + 1
    : Math.floor((showDate.year - 1) / NUM_OF_YEAR_IN_GRID) * NUM_OF_YEAR_IN_GRID + 1;
  let items = [];
  for (let i = firstYearOfGrid; i < firstYearOfGrid + NUM_OF_YEAR_IN_GRID; i++) {
    const isSelected = isDateValid(selectedDate) && showDate.era === selectedDate.era && i === selectedDate.year;
    const showTheYear = i <= eraDuration;
    items.push(
      <div
        key={i}
        className={`gridItem${isSelected ? " selected" : ""}${showTheYear ? "" : " hidden"}`}
        onClick={showTheYear ? () => onChange({ year: i, era: showDate.era }) : null}
      >
        {i}
      </div>
    );
  }
  return (
    <div style={getGridItemsStyle(NUM_OF_YEAR_IN_ROW)} className={`gridItems`}>
      {items}
    </div>
  );
};

const getMonthItems = (showDate, selectedDate, onChange, calendar) => {
  const era = showDate.era;
  const year = showDate.year;
  const items = calendar.months.map((month, monthIndex) => {
    const isSelected =
      isDateValid(selectedDate) &&
      era === selectedDate.era &&
      year === selectedDate.year &&
      selectedDate.month === monthIndex + 1;
    return (
      <div
        key={monthIndex}
        className={`gridItem${isSelected ? " selected" : ""}`}
        onClick={() => onChange({ month: monthIndex + 1, year: year, era: era })}
      >
        {month.shortName}
      </div>
    );
  });
  return (
    <div style={getGridItemsStyle(NUM_OF_MONTH_IN_ROW)} className={`gridItems`}>
      {items}
    </div>
  );
};

/**
 * Convert the number to be within the month count
 * @param {number} month
 * @param {number} monthCount
 */
const getMonthNumber = (month, monthCount) => ((month + monthCount - 1) % monthCount) + 1;

const getDayOnClick = (calendar, day, month, year, era, onChange, monthOffset = 0) => () => {
  const initDate = dateWithComponents(calendar, era, year, month, day, 0, 0, 0, Precision.day);
  const offsetDate =
    monthOffset === 0 ? initDate : dateByAddingSpan(calendar, initDate, TimespanRecord({ months: monthOffset }));
  onChange({
    era: offsetDate.era,
    year: offsetDate.year,
    month: offsetDate.month,
    day: offsetDate.day
  });
};

const getDayItems = (showDate, selectedDate, onChange, calendar) => {
  const monthCount = calendar.months.size;
  const era = showDate.era;
  const year = showDate.year;
  const month = showDate.month;
  const testDate = dateWithComponents(calendar, era, year, month, 1, 0, 0, 0, Precision.Auto);
  const daysBeforeFirstDay = testDate.weekday;
  const daysInMonth = getDaysInMonth(calendar, era, year, month);
  const prevMonth = getMonthNumber(month - 1, monthCount);
  const daysInPrevMonth = getDaysInMonth(calendar, era, year, prevMonth);
  const weekdaysCount = calendar.weekdays.size;
  const numberOfDaysInGrid = (Math.floor(daysInMonth / weekdaysCount) + 1) * weekdaysCount;

  let items = [];
  // get days of last month
  for (let i = daysInPrevMonth - daysBeforeFirstDay + 1; i <= daysInPrevMonth; i++) {
    items.push(
      <div key={-i} className="gridItem grey" onClick={getDayOnClick(calendar, i, month, year, era, onChange, -1)}>
        {i}
      </div>
    );
  }
  // get days of this month
  for (let i = 1; i <= daysInMonth; i++) {
    const isSelected =
      isDateValid &&
      era === selectedDate.era &&
      year === selectedDate.year &&
      month === selectedDate.month &&
      selectedDate.day === i;
    items.push(
      <div
        key={i}
        className={`gridItem${isSelected ? " selected" : ""}`}
        onClick={getDayOnClick(calendar, i, month, year, era, onChange, 0)}
      >
        {i}
      </div>
    );
  }

  // get days of the next month
  const daysToAttach = numberOfDaysInGrid - items.length + 1;
  for (let i = 1; i < daysToAttach; i++) {
    items.push(
      <div
        className="gridItem grey"
        key={items.length + i}
        onClick={getDayOnClick(calendar, i, month, year, era, onChange, 1)}
      >
        {i}
      </div>
    );
  }

  const headerItems = calendar.weekdays.map((wd, index) => (
    <div key={index} className="headerItem">
      {wd.shortName}
    </div>
  ));

  const gridItemsStyle = getGridItemsStyle(calendar.weekdays.size);

  return (
    <div className="calendarPickerGridItemsContainer">
      {/* TODO: Do not scroll the header with the days.
          Now the problem is the vertical scroll bar will
          make the header not matching the day items.
       */}
      {/* <div style={gridItemsStyle} className={`gridItems header`}>
        {headerItems}
      </div> */}
      <div style={gridItemsStyle} className={`gridItems`}>
        {headerItems}
        {items}
      </div>
    </div>
  );
};

// For iOS touch event
//#!if iOS === true
// const SWIPE_DELTA_THRESHOLD = 12;
// const SWIPE_VELOCITY_THRESHOLD = 0.3;
//#!endif

class CalendarPickerSelectorGrid extends PureComponent {
  constructor(props) {
    super(props);

    this.onDateChange = this.onDateChange.bind(this);

    this.state = {
      transition: sliderTransitions.DEFAULT,
      selectedPrecision: props.selectedPrecision,
      timestamp: props.date.timestamp
    };
    // For iOS touch event
    //#!if iOS === true
// this.gridRef = React.createRef();
// this._touchStartX = null;
// this._touchCurrentX = null;
// this._touchEventTimeout = null;
// this.onTouchStart = this.onTouchStart.bind(this);
// this.onTouchMove = this.onTouchMove.bind(this);
// 
// this.throttledTouchMove = throttle(this.onTouchMove, timeoutRates.IOS_CALENDAR_TOUCH_THROTTLE, {
// leading: false,
// trailing: true
// });
// 
    //#!endif
  }

  static getDerivedStateFromProps(props, state) {
    if (props.selectedPrecision !== state.selectedPrecision) {
      return {
        selectedPrecision: props.selectedPrecision,
        transition: sliderTransitions.FADE
      };
    }
    if (props.date.timestamp !== state.timestamp) {
      return {
        timestamp: props.date.timestamp,
        transition: sliderTransitions.DEFAULT
      };
    }
    return null;
  }

  // For iOS touch event
  //#!if iOS === true
// componentDidMount() {
// const node = this.gridRef.current;
// node.addEventListener("touchstart", this.onTouchStart);
// node.addEventListener("touchmove", this.throttledTouchMove);
// }
// 
// componentWillUnmount() {
// const node = this.gridRef.current;
// node.removeEventListener("touchstart", this.onTouchStart);
// node.removeEventListener("touchmove", this.throttledTouchMove);
// }
// 
// onTouchStart(e) {
// if (e && e.touches && e.touches.length > 0) {
// this._touchStartX = e.touches[0].screenX;
// this._touchStartTime = performance.now();
// this._touchGestureFired = false;
// }
// }
// 
// onTouchMove(e) {
// if (!this._touchGestureFired && e && e.touches && e.touches.length > 0) {
// const currentX = e.changedTouches[0].screenX;
// const deltaX = currentX - this._touchStartX;
// const deltaTime = performance.now() - this._touchStartTime;
// const velocity = deltaTime > 0 ? deltaX / deltaTime : 0;
// 
// if (Math.abs(deltaX) >= SWIPE_DELTA_THRESHOLD && Math.abs(velocity) >= SWIPE_VELOCITY_THRESHOLD) {
// if (deltaX > 0) {
// this.props.onPrevGrid();
// } else {
// this.props.onNextGrid();
// }
// this._touchGestureFired = true;
// }
// }
// }
// 
  //#!endif

  getGridItems(showDate, selectedDate, onChange) {
    const { calendar } = this.props;

    switch (this.props.selectedPrecision) {
      case Precision.Century:
        return getEraItems(showDate, selectedDate, onChange, calendar);
      case Precision.Year:
        return getYearItems(showDate, selectedDate, onChange, calendar);
      case Precision.Month:
        return getMonthItems(showDate, selectedDate, onChange, calendar);
      case Precision.Day:
      case Precision.Hour:
      case Precision.Minute:
      case Precision.Second:
        return getDayItems(showDate, selectedDate, onChange, calendar);
      default:
        return null;
    }
  }

  onDateChange(changes) {
    const { date, calendar, selectedPrecision, onChange } = this.props;
    // Create a date of timestamp Zero
    // if no date available
    const newMergedDate = (!isNil(date) && !isNil(date.timestamp)
      ? date
      : dateWithTimestamp(calendar, 0, selectedPrecision)
    ).merge(changes);
    // Regenerate the date record
    // by the merged changes
    onChange(
      dateWithComponents(
        calendar,
        newMergedDate.era,
        newMergedDate.year,
        newMergedDate.month,
        newMergedDate.day,
        newMergedDate.hour,
        newMergedDate.minute,
        newMergedDate.second,
        newMergedDate.precision
      )
    );
  }

  render() {
    const { date, selectedDate, calendar } = this.props;
    const { selectedPrecision, transition } = this.state;
    return (
      <div className={`calendarPickerSelectorGrid`} ref={this.gridRef}>
        <PageSlider
          pageId={`${selectedPrecision}-${date.timestamp}`}
          pageIndex={getGridIndex(date, selectedPrecision, calendar)}
          transition={transition}
        >
          {this.getGridItems(date, selectedDate, this.onDateChange)}
        </PageSlider>
      </div>
    );
  }
}

CalendarPickerSelectorGrid.defaultProps = {
  showSelected: true
};

CalendarPickerSelectorGrid.propTypes = {
  calendar: PropTypeShapes.calendar.isRequired,
  date: PropTypeShapes.dateRelated,
  onChange: PropTypes.func,
  selectedPrecision: PropTypes.number.isRequired,
  showSelected: PropTypes.bool.isRequired,
  selectedDate: PropTypeShapes.dateRelated,

  // For iOS touch event
  //#!if iOS === true
// onNextGrid: PropTypes.func,
// onPrevGrid: PropTypes.func
  //#!endif
};

export default CalendarPickerSelectorGrid;
